
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { 
  Truck, MapPin, Globe, Satellite, 
  Zap, Battery, Signal, Search, 
  MoreVertical, Radio, 
  Monitor, Settings2, Power, Lock, Unlock,
  Pencil, FileText, User, Navigation, Locate,
  X, CarFront, UserCog, Loader2, FileSpreadsheet
} from 'lucide-react';
import { Order } from './types';
import { Badge } from './SharedComponents';
import L from 'leaflet';

export const LogisticsMonitorPage = ({ orders, clients, onSelectOrder, onExit }: any) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  
  const [isLive] = useState(true);
  const [mapStyle, setMapStyle] = useState<'streets' | 'topo' | 'satellite'>('streets');
  const [activeMenu, setActiveMenu] = useState<{id: string, x: number, y: number} | null>(null);
  const [statusFilter, setStatusFilter] = useState('GERAL');
  
  // Modais
  const [selectedVehicle, setSelectedVehicle] = useState<Order | null>(null);
  const [isCommandModalOpen, setIsCommandModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPoiModalOpen, setIsPoiModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [telemetry, setTelemetry] = useState<Record<string, { 
    speed: number, 
    ignition: boolean, 
    blocked: boolean,
    lat: number,
    lng: number 
  }>>({});

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const liveFleet = useMemo(() => orders.filter((o: Order) => o.dataEvento === today), [orders, today]);

  // Simulação de Telemetria
  useEffect(() => {
    let interval: any;
    if (isLive && liveFleet.length > 0) {
      interval = setInterval(() => {
        setTelemetry(prev => {
          const next = { ...prev };
          liveFleet.forEach((order: Order) => {
            const current = next[order.id] || { speed: 0, ignition: true, blocked: false, lat: -23.5015, lng: -47.4526 };
            const isMoving = Math.random() > 0.1 && !current.blocked;
            next[order.id] = {
              ...current,
              speed: isMoving ? Math.floor(Math.random() * 60) + 20 : 0,
              lat: current.lat + (isMoving ? (Math.random() - 0.5) * 0.0005 : 0),
              lng: current.lng + (isMoving ? (Math.random() - 0.5) * 0.0005 : 0)
            };
          });
          return next;
        });
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isLive, liveFleet]);

  // Mapa Init
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = L.map(mapContainerRef.current, { zoomControl: false, attributionControl: false }).setView([-23.5015, -47.4526], 13);
      const resizeObserver = new ResizeObserver(() => mapRef.current?.invalidateSize());
      resizeObserver.observe(mapContainerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      if (tileLayerRef.current) mapRef.current.removeLayer(tileLayerRef.current);
      let url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
      if (mapStyle === 'satellite') url = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
      tileLayerRef.current = L.tileLayer(url, { maxZoom: 19 }).addTo(mapRef.current);
    }
  }, [mapStyle]);

  // Marcadores
  useEffect(() => {
    if (mapRef.current) {
      liveFleet.forEach((order: Order) => {
        const data = telemetry[order.id];
        if (!data) return;
        const icon = L.divIcon({
          className: 'custom-icon', 
          html: `
            <div class="relative flex flex-col items-center">
              <div class="${data.blocked ? 'bg-red-600' : 'bg-primary'} text-white p-2.5 rounded-xl border-2 border-white shadow-xl transition-all duration-500">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-2.235-2.794A2 2 0 0 0 17.99 9H15v9"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>
              </div>
              <div class="mt-1 bg-secondary text-white text-[9px] px-2 py-0.5 rounded font-black border border-white shadow-lg whitespace-nowrap">${order.placa || 'FROTA'}</div>
            </div>
          `,
          iconSize: [40, 40], iconAnchor: [20, 20]
        });
        if (markersRef.current[order.id]) {
          markersRef.current[order.id].setLatLng([data.lat, data.lng]);
        } else {
          markersRef.current[order.id] = L.marker([data.lat, data.lng], { icon }).addTo(mapRef.current!).on('click', () => onSelectOrder(order));
        }
      });
    }
  }, [liveFleet, telemetry, onSelectOrder]);

  const handleViewInMap = (order: Order) => {
    const data = telemetry[order.id];
    if (data && mapRef.current) mapRef.current.flyTo([data.lat, data.lng], 17, { duration: 1.5 });
    setActiveMenu(null);
  };

  const handleDistance = (order: Order) => {
    const data = telemetry[order.id];
    if (data) {
      const sorocabaHQ = L.latLng(-23.5015, -47.4526);
      const vehiclePos = L.latLng(data.lat, data.lng);
      const dist = (sorocabaHQ.distanceTo(vehiclePos) / 1000).toFixed(1);
      alert(`O veículo está a ${dist}km de Sorocaba.`);
    }
    setActiveMenu(null);
  };

  const handleExportDatalog = (order: Order) => {
    setSelectedVehicle(order);
    setIsSaving(true);
    const data = telemetry[order.id];
    
    setTimeout(() => {
      const csvRows = [
        ["sep=;"],
        ["Data/Hora", "Latitude", "Longitude", "Velocidade", "Evento", "Status Motor"],
        [
          new Date().toLocaleString('pt-BR'), 
          `${data.lat.toFixed(6)}`, 
          `${data.lng.toFixed(6)}`, 
          `${data.speed}km/h`, 
          order.nomeEvento || "Operação Logística",
          data.blocked ? "Bloqueado" : "Liberado"
        ]
      ];

      const csvContent = csvRows.map(row => 
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";")
      ).join("\r\n");

      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Datalog_EventLog_${order.placa}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setIsSaving(false);
    }, 1500);
  };

  return (
    <div className="h-screen bg-[#f1f5f9] flex flex-col overflow-hidden animate-in fade-in duration-500">
      
      {/* Header Estilo Radar */}
      <header className="h-14 bg-[#0f172a] text-white flex justify-between items-center px-6 shrink-0 z-[100] shadow-xl">
         <div className="flex items-center gap-4">
            <Monitor size={18} className="text-primary" />
            <h1 className="text-sm font-black uppercase tracking-widest italic">Torre de Comando GPRS</h1>
            <div className="flex bg-white/5 p-1 rounded-lg gap-1 ml-4">
               <button onClick={() => setStatusFilter('GERAL')} className={`px-4 py-1.5 rounded-md text-[9px] font-black uppercase transition-all ${statusFilter === 'GERAL' ? 'bg-primary text-white shadow-lg' : 'text-white/40'}`}>Geral</button>
               <button onClick={() => setStatusFilter('ROTA')} className={`px-4 py-1.5 rounded-md text-[9px] font-black uppercase transition-all ${statusFilter === 'ROTA' ? 'bg-primary text-white shadow-lg' : 'text-white/40'}`}>Em Rota</button>
               <button onClick={() => setStatusFilter('PARADOS')} className={`px-4 py-1.5 rounded-md text-[9px] font-black uppercase transition-all ${statusFilter === 'PARADOS' ? 'bg-primary text-white shadow-lg' : 'text-white/40'}`}>Parados</button>
            </div>
         </div>
         <div className="flex items-center gap-4">
            <Badge className="bg-secondary text-white border-none px-3 py-1 font-black text-[9px] uppercase italic">Protocolo Crele/GT06 Homologado</Badge>
            <button onClick={onExit} className="p-2 hover:bg-white/10 rounded-xl transition-all"><X size={20} /></button>
         </div>
      </header>

      {/* Área do Mapa */}
      <section className="relative flex-[0.6] bg-slate-200 overflow-hidden z-10">
         <div ref={mapContainerRef} className="w-full h-full" />
         <div className="absolute top-4 right-4 z-[50] flex flex-col gap-2">
            <button onClick={() => setMapStyle('streets')} className={`p-3 rounded-xl shadow-xl transition-all ${mapStyle === 'streets' ? 'bg-primary text-white' : 'bg-white text-slate-400'}`}><Globe size={20} /></button>
            <button onClick={() => setMapStyle('satellite')} className={`p-3 rounded-xl shadow-xl transition-all ${mapStyle === 'satellite' ? 'bg-primary text-white' : 'bg-white text-slate-400'}`}><Satellite size={20} /></button>
         </div>
      </section>

      {/* Tabela de Monitoramento */}
      <section className="flex-[0.4] bg-white border-t border-slate-200 flex flex-col shrink-0 shadow-[0_-20px_50px_rgba(0,0,0,0.1)] z-[60] overflow-visible">
         <div className="flex-1 overflow-auto scrollbar-hide overflow-visible">
            <table className="w-full text-left border-collapse min-w-[1200px]">
               <thead className="sticky top-0 bg-white z-20 border-b border-slate-100">
                  <tr className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                     <th className="px-6 py-5 w-16 text-center">Status</th>
                     <th className="px-4 py-5">Veículo / Placa</th>
                     <th className="px-4 py-5">Sinal / Bateria</th>
                     <th className="px-4 py-5">Localização Atual</th>
                     <th className="px-4 py-5 text-center">Velocidade</th>
                     <th className="px-4 py-5">Evento Vinculado</th>
                     <th className="px-6 py-5 text-right">Ações</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-50">
                  {liveFleet.map((order: Order) => {
                    const data = telemetry[order.id] || { speed: 0, ignition: false, blocked: false, lat: -23.5015, lng: -47.4526 };
                    const client = clients.find((c:any) => c.id === order.clientId);
                    return (
                      <tr key={order.id} className="hover:bg-primary/[0.02] transition-all group overflow-visible">
                         <td className="px-6 py-6 text-center">
                            <div className={`w-3 h-3 rounded-full mx-auto ${data.blocked ? 'bg-red-500' : data.speed > 0 ? 'bg-green-500' : 'bg-slate-300'} shadow-sm`}></div>
                         </td>
                         <td className="px-4 py-6">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{order.placa || '---------'}</p>
                            <p className="text-[12px] font-black text-slate-800 uppercase italic mt-0.5 leading-none">{order.veiculoModelo || 'Caminhão Logístico'}</p>
                         </td>
                         <td className="px-4 py-6">
                            <div className="flex items-center gap-3">
                               <div className="flex items-center gap-1 text-primary"><Signal size={14} strokeWidth={3} /><span className="text-[10px] font-black">98%</span></div>
                               <div className="flex items-center gap-1 text-green-500"><Battery size={14} strokeWidth={3} /><span className="text-[10px] font-black">100%</span></div>
                            </div>
                         </td>
                         <td className="px-4 py-6">
                            <p className="text-[11px] font-bold text-slate-600 truncate max-w-[280px]">{client?.enderecoCompleto || 'Rastreando sinal via LTE...'}</p>
                            <p className="text-[9px] text-primary font-black uppercase flex items-center gap-1 mt-1"><MapPin size={10} strokeWidth={3} /> {client?.cidade || 'SOROCABA'}</p>
                         </td>
                         <td className="px-4 py-6 text-center">
                            <Badge className="bg-green-50 text-green-600 border-none px-4 py-1.5 font-black text-[11px] rounded-full shadow-sm">{data.speed} Km/h</Badge>
                         </td>
                         <td className="px-4 py-6">
                            <p className="text-[10px] font-black text-primary uppercase tracking-tight truncate max-w-[150px]">{order.nomeEvento}</p>
                         </td>
                         <td className="px-6 py-6 text-right overflow-visible">
                            <div className="flex items-center justify-end gap-3 overflow-visible">
                               <button 
                                onClick={() => handleExportDatalog(order)}
                                className="bg-white px-5 py-3 rounded-2xl flex items-center gap-3 shadow-[0_10px_25px_rgba(0,0,0,0.1)] border border-slate-50 hover:shadow-2xl transition-all group shrink-0 active:scale-95"
                               >
                                  {isSaving && selectedVehicle?.id === order.id ? (
                                    <Loader2 size={16} className="text-primary animate-spin" />
                                  ) : (
                                    <FileSpreadsheet size={16} className="text-primary group-hover:scale-110 transition-transform" />
                                  )}
                                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Datalog</span>
                               </button>

                               {/* BOTÃO DE AÇÃO - FIXING POPUP OVERFLOW */}
                               <div className="relative">
                                  <button 
                                   onClick={(e) => { 
                                     const rect = e.currentTarget.getBoundingClientRect();
                                     setSelectedVehicle(order); 
                                     setActiveMenu(activeMenu?.id === order.id ? null : { id: order.id, x: rect.left - 240, y: rect.top - 320 }); 
                                   }}
                                   className={`w-11 h-11 rounded-full flex items-center justify-center transition-all shadow-[0_8px_30px_rgba(0,168,224,0.4)] ${activeMenu?.id === order.id ? 'bg-secondary' : 'bg-primary'} text-white active:scale-90`}
                                  >
                                    <MoreVertical size={22} />
                                  </button>
                                  
                                  {activeMenu?.id === order.id && (
                                    <>
                                      <div className="fixed inset-0 z-[1000]" onClick={() => setActiveMenu(null)}></div>
                                      <div 
                                        className="fixed w-64 bg-white border border-slate-100 rounded-[32px] shadow-[0_40px_100px_rgba(0,0,0,0.3)] z-[2000] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                                        style={{ top: activeMenu.y, left: activeMenu.x }}
                                      >
                                         <div className="p-3 space-y-1">
                                            <div className="px-5 py-3 border-b border-slate-50 mb-1 bg-slate-50/30">
                                               <p className="text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">Controle GPRS</p>
                                               <p className="text-[11px] font-black text-secondary truncate uppercase">{order.placa}</p>
                                            </div>
                                            <MenuAction icon={Search} label="VER NO MAPA" onClick={() => handleViewInMap(order)} />
                                            <MenuAction icon={Navigation} label="CALCULAR DISTÂNCIA" onClick={() => handleDistance(order)} />
                                            <MenuAction icon={MapPin} label="REGISTRAR POI LOCAL" onClick={() => { setSelectedVehicle(order); setIsPoiModalOpen(true); setActiveMenu(null); }} />
                                            <MenuAction icon={Zap} label="COMANDOS REMOTOS" onClick={() => { setSelectedVehicle(order); setIsCommandModalOpen(true); setActiveMenu(null); }} />
                                            <MenuAction icon={User} label="STREET VIEW" onClick={() => { window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${data.lat},${data.lng}`, '_blank'); setActiveMenu(null); }} />
                                            <div className="h-[1px] bg-slate-50 my-1 mx-3"></div>
                                            <MenuAction icon={Pencil} label="EDITAR VEÍCULO" onClick={() => { setSelectedVehicle(order); setIsEditModalOpen(true); setActiveMenu(null); }} />
                                            <MenuAction icon={FileText} label="RELATÓRIO DE TRAJETO" onClick={() => { handleExportDatalog(order); setActiveMenu(null); }} />
                                         </div>
                                      </div>
                                    </>
                                  )}
                               </div>
                            </div>
                         </td>
                      </tr>
                    );
                  })}
               </tbody>
            </table>
         </div>
      </section>

      {/* MODAL: COMANDOS REMOTOS (BLOQUEIO) */}
      {isCommandModalOpen && selectedVehicle && (
        <div className="fixed inset-0 bg-secondary/95 backdrop-blur-md z-[5000] flex items-center justify-center p-6 animate-in fade-in">
           <div className="bg-white rounded-[50px] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95">
              <div className="p-10 bg-slate-900 text-white flex flex-col items-center gap-6">
                 <div className={`p-6 rounded-[32px] shadow-2xl transition-all ${telemetry[selectedVehicle.id]?.blocked ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`}>
                    {telemetry[selectedVehicle.id]?.blocked ? <Lock size={48} /> : <Unlock size={48} />}
                 </div>
                 <div className="text-center space-y-2">
                    <h3 className="text-2xl font-black uppercase tracking-tight italic">Controle GPRS</h3>
                    <p className="text-[10px] text-primary font-bold uppercase tracking-widest">Placa: {selectedVehicle.placa}</p>
                 </div>
              </div>
              <div className="p-10 space-y-6">
                 <button 
                  disabled={isSaving}
                  onClick={() => {
                    setIsSaving(true);
                    setTimeout(() => {
                      setTelemetry(prev => ({ ...prev, [selectedVehicle.id]: { ...prev[selectedVehicle.id], blocked: !prev[selectedVehicle.id].blocked, speed: 0 } }));
                      setIsSaving(false);
                      setIsCommandModalOpen(false);
                    }, 1500);
                  }}
                  className={`w-full py-6 rounded-[32px] font-black uppercase text-xs tracking-widest transition-all shadow-xl flex items-center justify-center gap-3 ${telemetry[selectedVehicle.id]?.blocked ? 'bg-green-600 text-white shadow-green-100' : 'bg-red-600 text-white shadow-red-100'}`}
                 >
                    {isSaving ? <Loader2 className="animate-spin" size={20} /> : <Power size={20} />}
                    {isSaving ? 'Transmitindo...' : telemetry[selectedVehicle.id]?.blocked ? 'Ativar Motor' : 'Bloquear Motor'}
                 </button>
                 <button onClick={() => setIsCommandModalOpen(false)} className="w-full py-2 text-[9px] font-black text-slate-300 uppercase tracking-widest hover:text-secondary transition-colors">Cancelar Operação</button>
              </div>
           </div>
        </div>
      )}

      {/* MODAL: EDITAR VEÍCULO */}
      {isEditModalOpen && selectedVehicle && (
        <div className="fixed inset-0 bg-secondary/90 backdrop-blur-md z-[6000] flex items-center justify-center p-6 animate-in fade-in">
           <div className="bg-white rounded-[48px] w-full max-w-md shadow-[0_50px_100px_rgba(0,0,0,0.5)] overflow-hidden animate-in zoom-in-95">
              <div className="p-10 bg-[#f59e0b] text-white flex justify-between items-center relative">
                 <div className="flex items-center gap-4">
                    <div className="bg-white/20 p-2.5 rounded-xl shadow-inner">
                       <Truck size={24} strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-black uppercase italic tracking-tight leading-none">Editar Veículo</h2>
                 </div>
                 <button onClick={() => setIsEditModalOpen(false)} className="p-2 hover:bg-white/20 rounded-xl transition-all"><X size={32} strokeWidth={2.5} /></button>
              </div>
              
              <div className="p-10 space-y-8 bg-white">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-3">Placa Mercosul</label>
                    <div className="relative group">
                       <CarFront size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-amber-500" />
                       <input 
                        className="w-full bg-[#f8fafc] border border-slate-100 p-5 pl-14 rounded-3xl outline-none font-black text-2xl uppercase text-secondary tracking-widest shadow-inner focus:ring-4 focus:ring-amber-500/5 transition-all text-center" 
                        defaultValue={selectedVehicle.placa} 
                        placeholder="ABC-1234"
                       />
                    </div>
                 </div>

                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-3">Modelo do Veículo</label>
                    <input 
                      className="w-full bg-[#f8fafc] border border-slate-100 p-5 rounded-3xl outline-none font-bold text-secondary focus:ring-4 focus:ring-amber-500/5 transition-all shadow-inner" 
                      defaultValue={selectedVehicle.veiculoModelo || 'Caminhão Logístico'} 
                      placeholder="Ex: Mercedes-Benz Accelo"
                    />
                 </div>

                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-3">Motorista Responsável</label>
                    <div className="relative">
                       <UserCog size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" />
                       <input 
                        className="w-full bg-[#f8fafc] border border-slate-100 p-5 pl-14 rounded-3xl outline-none font-bold text-secondary focus:ring-4 focus:ring-amber-500/5 transition-all shadow-inner" 
                        defaultValue={selectedVehicle.responsavelInterno} 
                        placeholder="Nome do condutor"
                       />
                    </div>
                 </div>

                 <button 
                  className="w-full py-6 bg-[#222e3d] text-white rounded-[32px] font-black uppercase text-xs tracking-[0.2em] shadow-2xl active:scale-95 transition-all hover:bg-black" 
                  onClick={() => { alert('Unidade atualizada com sucesso!'); setIsEditModalOpen(false); }}
                 >
                    Atualizar Unidade
                 </button>
              </div>
           </div>
        </div>
      )}

    </div>
  );
};

const MenuAction = ({ icon: Icon, label, onClick }: any) => (
  <button 
    onClick={onClick} 
    className="w-full text-left px-5 py-3.5 text-[11px] font-black uppercase text-[#42a2d1] hover:bg-primary/5 transition-all flex items-center gap-4 group rounded-2xl active:scale-[0.98]"
  >
    <Icon size={16} strokeWidth={2.5} className="group-hover:scale-110 transition-transform" />
    <span className="tracking-tight">{label}</span>
  </button>
);
